import { reactive } from 'vue';
import { defineStore } from 'pinia';
import { GetDeletionBusinessAdapter } from '../adapters/getDeletionBusinessAdapter';
import { IDeletionBusinessState } from './interfaces/deletionBusiness';
import { TableContentHeader, TableContentProperty } from '@/corePagesModule/BaseTableContent/interface/tableContentHeaders';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { IInventoryRepository } from '@/coreInfrastructureModule/interfaces/IInventoryRepository';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { GetDeletionBusinessUseCase } from '../useCases/getDeletionBusinessUseCase';
import { IDeletionBusiness } from './interfaces/entities/IDeletionBusiness';
import { TableContentItem } from '@/corePagesModule/BaseTableContent/interface/tableContentItems';
import { TrashBinRepository } from '@/coreInfrastructureModule/Repositories/TrashBinRepository';
import { i18n } from '@/i18n';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { openLoadingModal } from '@/corePagesModule/components/modal/modalActions';
import { HEADER_MODIFY_TABLE_CONTENT_PROPERTY } from '@/corePagesModule/BaseTableContent/constants';
import { diContainer } from '@/coreContainer/container';
import { useTableFilter } from '@/corePagesModule/composables/useTableFilter';
import { simpleCopy } from '@/utils/deepCopy';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';

/**
 * DeletionBusinessStore（引数でDIする）
 * @param inventoryRepository - 削除ページのrepository
 * @param getDeletionBusinessUseCase - 削除ページのUseCase
 * @param adapter - アダプタ
 */
export const useDeletionBusinessStore = (
  inventoryRepository: IInventoryRepository,
  getDeletionBusinessUseCase: GetDeletionBusinessUseCase,
  adapter: GetDeletionBusinessAdapter
) =>
  defineStore('useDeletionBusinessStore', () => {
    const trashBinRepository = new TrashBinRepository();

    const t = i18n.t;

    const state = reactive<IDeletionBusinessState>({
      renderingKey: 0,
      tableContentProperties: {} as TableContentHeader,
      tableContentItems: [],
      loadingState: {
        status: {
          type: 'loading',
        },
      },
      isRefreshTable: false,
      pagingOptions: initPagingOptions(30),
      sortKey: 'id',
      isAsc: true,
      filterState: [],
      checkedIds: [],
      deleteBusinessModalState: false,
    });

    // フィルター情報のComposable
    const { cachedFilterItems, bulkUpdateFilterInfos, getFilterItemsValue, initTableFilter } = useTableFilter('deletionBusinessTable');

    /**
     * stateを初期化する
     */
    const $reset = () => {
      state.renderingKey = 0;
      state.tableContentProperties = {} as TableContentHeader;
      state.tableContentItems = [];
      state.loadingState = {
        status: {
          type: 'loading',
        },
      };
      state.isRefreshTable = false;
      state.pagingOptions = initPagingOptions(30);
      state.sortKey = 'id';
      state.isAsc = true;
      state.filterState = [];
      state.checkedIds = [];
      state.deleteBusinessModalState = false;
    };

    /**
     * 初期化処理
     */
    const init = async () => {
      $reset();
      state.tableContentProperties = fetchTableContentProperties();
      await fetchTableData();
      state.renderingKey += 1;
    };

    /**
     * データを取得する
     */
    const fetchTableData = async () => {
      state.loadingState.status.type = 'loading';
      await initTableFilter();
      // FIXME: これ、海外対応がどうなるのかが分からん(リージョンによって情報が変わるかも)
      // 期間情報の作成 FIXME: とりあえずtodayから固定で出しているが、そのうち選択UI作成して適応させる
      const earlyPeriodYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(-2);
      const earlyPeriod = DateService.convertDateStrToDateWithHyphen(
        new Date(earlyPeriodYearMonth.year, earlyPeriodYearMonth.month - 1, 1)
      );
      const latterPeriodYearMonth = DateService.getShiftedIYearMonthFromNowDateByMonths(3);
      const latterPeriod = DateService.convertDateStrToDateWithHyphen(
        new Date(latterPeriodYearMonth.year, latterPeriodYearMonth.month - 1, 1)
      );

      const useFilterItems = cachedFilterItems.value;

      const response = await getDeletionBusinessUseCase.Execute(
        inventoryRepository,
        state.isAsc ? 'ASC' : 'DESC',
        state.pagingOptions.pageSize,
        state.pagingOptions.offset,
        earlyPeriod, // NOTE: どうせ使わないので、とりあえず当日突っ込んどけ
        latterPeriod,
        state.sortKey ?? 'id',
        useFilterItems,
        adapter
      );
      switch (response.status.type) {
        case 'success':
          state.tableContentItems = convertDeletionBusinessToTableContentItems(response.status.data.result);
          state.pagingOptions = response.status.data.pagingOptions;

          // 取得した情報数が0の場合、ゼロマッチステータスにする
          if (state.tableContentItems.length === 0) {
            state.loadingState.status.type = 'zeroMatch';
          } else {
            state.loadingState.status.type = 'success';
          }
          break;
        case 'error':
        default:
          state.loadingState.status.type = 'error';
          return null;
      }
    };

    /**
     * 取引削除ページ用のデータをテーブル表示用の情報に変換する
     * @param deletionBusinesses - 取引情報一覧
     */
    const convertDeletionBusinessToTableContentItems = (deletionBusinesses: IDeletionBusiness[]) => {
      const displayTableContent: TableContentItem[] = [];
      deletionBusinesses.forEach((deletionBusiness) => {
        displayTableContent.push({
          displayId: deletionBusiness.businessId,
          isVirtualChild: false,
          ...deletionBusiness,
        });
      });
      return displayTableContent;
    };

    /**
     * テーブルのデータをソートする
     * @param sortKey - ソートキー
     * @param isAsc - 昇順かどうか
     */
    const sortTableContentItems = async (sortKey: TableContentProperty, isAsc: boolean) => {
      let sortTarget = sortKey.key;
      switch (sortKey.key) {
        case 'businessId':
          sortTarget = 'aggregateBusinessId';
          break;
        case 'supplierForecastResponsibleUser':
          sortTarget = 'aggregateBusinessResponsibleUser';
          break;
        case 'customerForecastResponsibleUser':
          sortTarget = 'baseBusinessResponsibleUser';
          break;
        case 'businessUpdateAt':
          sortTarget = 'updateAt';
          break;
      }
      state.sortKey = sortTarget;
      state.isAsc = isAsc;
      state.tableContentProperties.contentProperties = state.tableContentProperties.contentProperties.map((property) => {
        property.sortOption.isAsc = property.key === sortKey.key ? isAsc : true;
        return property;
      });
      await fetchTableData();
    };

    /**
     * テーブルのデータを再取得する
     */
    const reloadTable = async () => {
      await fetchTableData();
    };

    /**
     * オフセットをリセットする
     */
    const resetOffset = () => {
      // pagingOptionsがnullableなのでこうしてます
      if (state.pagingOptions) {
        state.pagingOptions.offset = 0;
      }
    };

    /**
     * ページング変更時の処理
     * @param pagingOptions - ページングオプション
     */
    const onChangePaging = async (pagingOptions: IPagingOptions) => {
      state.pagingOptions = {
        pageSize: pagingOptions.pageSize,
        offset: pagingOptions.offset,
        totalResultsAvailable: pagingOptions.totalResultsAvailable,
        returnCount: pagingOptions.returnCount,
      };
      await fetchTableData();
    };

    /**
     * テーブルのヘッダー情報を取得
     */
    const fetchTableContentProperties = (): TableContentHeader => {
      return {
        contentProperties: [
          {
            key: 'choice',
            isVariableWidth: false,
            isNarrowable: false,
            isEditable: false,
            sortOption: { isSortable: false, isAsc: true },
            propertyPath: { paths: ['choice'], propertyType: 'string' },
          },
          // TODO: 展開機能が無いので、一旦コメントアウト
          // {
          //   key: 'expansion',
          //   isVariableWidth: false,
          //   isNarrowable: false,
          //   isEditable: false,
          //   sortOption: { isSortable: false, isAsc: true },
          //   headerWidth: 32,
          //   propertyPath: { paths: ['expansion'], propertyType: 'string' },
          // },
          {
            key: 'businessId',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['businessId'], propertyType: 'string' },
          },
          {
            key: 'warehouseNumber',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['warehouseNumber'], propertyType: 'string' },
          },
          {
            key: 'warehouse',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['warehouse'], propertyType: 'string' },
          },
          {
            key: 'supplierNumber',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['supplierNumber'], propertyType: 'string' },
          },
          {
            key: 'supplierName',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['supplierName'], propertyType: 'string' },
          },
          {
            key: 'businessNumber',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['businessNumber'], propertyType: 'string' },
          },
          {
            key: 'businessName',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['businessName'], propertyType: 'string' },
          },
          {
            key: 'package',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['package'], propertyType: 'number' },
          },
          {
            key: 'unit',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['unit'], propertyType: 'string' },
          },
          {
            key: 'customerNumber',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: false,
              isAsc: true,
            },
            propertyPath: { paths: ['customerNumber'], propertyType: 'string' },
          },
          {
            key: 'customerName',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: false,
              isAsc: true,
            },
            propertyPath: { paths: ['customerName'], propertyType: 'string' },
          },
          {
            key: 'salesDestinationNumber',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: false,
              isAsc: true,
            },
            propertyPath: { paths: ['salesDestinationNumber'], propertyType: 'string' },
          },
          {
            key: 'salesDestinationName',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: false,
              isAsc: true,
            },
            propertyPath: { paths: ['salesDestinationName'], propertyType: 'string' },
          },
          {
            key: 'manufacturer',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['manufacturer'], propertyType: 'string' },
          },
          {
            key: 'endUser',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['endUser'], propertyType: 'string' },
          },
          {
            key: 'supplierForecastResponsibleUser',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['responsibleUserName', 'supplierForecast'], propertyType: 'string' },
          },
          {
            key: 'customerForecastResponsibleUser',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: false,
              isAsc: true,
            },
            propertyPath: { paths: ['responsibleUserName', 'customerForecast'], propertyType: 'string' },
          },
          {
            key: 'businessUpdateAt',
            isVariableWidth: true,
            isNarrowable: true,
            isEditable: false,
            sortOption: {
              isSortable: true,
              isAsc: true,
            },
            propertyPath: { paths: ['businessUpdateAt'], propertyType: 'string' },
          },
          HEADER_MODIFY_TABLE_CONTENT_PROPERTY,
        ],
        options: {
          stableColumnCount: 1,
          resizableHeaderColumn: true,
        },
      };
    };

    /**
     * 取引を削除するUseCaseを実行する関数(モーダルから呼び出すためにモックしている)
     * @param businessIds - 削除する取引IDのリスト
     */
    const deleteDeletionAggregateBusiness = async (businessIds: string[]) => {
      return await diContainer.deleteDeletionAggregateBusinessUseCase.Execute(businessIds);
    };

    /**
     * 取引の削除処理を実行する
     */
    const deleteBusiness = async () => {
      // 取引削除モーダルのステータスを変更する
      state.deleteBusinessModalState = false;

      // 以下、取引削除処理
      // const response = await deleteDeletionBusinessUseCase.Execute(trashBinRepository, state.checkedIds);
      const response = await openLoadingModal(
        t('取引削除ローディングモーダルタイトル'),
        deleteDeletionAggregateBusiness,
        [state.checkedIds],
        t('変更内容で更新中です。しばらくお待ちください。')
      );
      switch (response.status.type) {
        case 'success':
          state.loadingState.status.type = 'success';

          // API成功時にテーブルのデータを再取得する
          await fetchTableData();

          // API成功時にチェックボックスの選択状態をリセットする
          state.checkedIds = [];

          // トースト表示
          toast.success(t('削除に成功しました。'));
          break;
        case 'error':
          toast.error(t('削除に失敗しました。'));
        default:
          state.loadingState.status.type = 'error';
          return null;
      }
    };

    /**
     * フィルターモーダルの適用ボタンクリック時の処理
     * @param filterItems - フィルターモーダル内で選択された項目
     */
    const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
      // フィルターモーダルの選択情報を更新
      await bulkUpdateFilterInfos(simpleCopy(filterItems));
      // オフセットをリセットしてから表示内容を更新
      resetOffset();
      await reloadTable();
    };

    return {
      state,
      $reset,
      init,
      onChangePaging,
      sortTableContentItems,
      reloadTable,
      resetOffset,
      deleteBusiness,
      onAppliedFilterModal,
      getFilterItemsValue,
    };
  });
